import { FC, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import {
  CloseModalButton,
  BackModalButton,
  MainButton,
} from "components/Buttons";
import BasicModal from "./BasicModal";
import { ModalHeading, ModalContent, InfoDecorative } from "./styled";
import { AppText } from "components";
import {
  authStepUp,
  waitForOneTimeAccessToken,
  setupMFAVerification,
} from "api/auth";
import { getCardSensitiveData } from "api/cardProduct";
import { GlobalStore, UserStore } from "stores";

import { ReactComponent as LockIcon } from "assets/icons/lock-unlocked.svg";

interface CardPinModalProps {
  cardId?: number | null;
  isOpen: boolean;
  onClose: () => void;
}
const CardPinModal: FC<CardPinModalProps> = ({ cardId, onClose, isOpen }) => {
  const { t } = useTranslation();

  const { isMFAEnabled, authOneTimeAccessToken } = UserStore;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cardPin, setCardPin] = useState<string>("");

  const change2Fa = useCallback(() => {
    setIsLoading(true);
    setupMFAVerification().finally(() => setIsLoading(false));
  }, [setIsLoading]);

  const getPinCode = async () => {
    if (!cardId) {
      return;
    }
    try {
      await authStepUp("STEP_UP_CARD");

      await waitForOneTimeAccessToken();
      setIsLoading(true);

      const { data } = await getCardSensitiveData(
        cardId,
        authOneTimeAccessToken!
      );
      setCardPin(data.pinCode);
    } catch (err: any) {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BasicModal
      isOpen={isOpen}
      onExited={() => setCardPin("")}
      onClose={onClose}
      showLoader={isLoading}
    >
      <ModalContent>
        <BackModalButton onBack={onClose} label="back" />
        <CloseModalButton onClose={onClose} label="close" />
        <ModalHeading $centered>{t("CARD_PIN_CODE")}</ModalHeading>

        <InfoDecorative>
          {!isMFAEnabled ? (
            <>
              <AppText fontSize={18} fontWeight={600}>
                {t("TWO_FA_FOR_PIN")}
              </AppText>

              <MainButton
                maxWidth="14rem"
                style={{ marginTop: "1.6rem" }}
                variant="contained"
                onClick={() => change2Fa()}
              >
                <LockIcon />
                {t("ENABLE")}
              </MainButton>
            </>
          ) : (
            <>
              {cardPin && (
                <>
                  <AppText fontSize={30} fontWeight={600}>
                    {cardPin || "****"}
                  </AppText>
                  <AppText fontSize={12} fontWeight={400}>
                    {t("KEEP_PIN_CODE_SAFE")}
                  </AppText>
                </>
              )}
            </>
          )}
        </InfoDecorative>
      </ModalContent>
    </BasicModal>
  );
};

export default observer(CardPinModal);
